import { Cookies as ReactCookies } from "react-cookie";
import { getExpirationDate } from "./helpers";
import { Preferences } from "@capacitor/preferences";

// Helper function to detect if we're in a Capacitor environment
const isCapacitorEnvironment = () => {
  try {
    return window.Capacitor && window.Capacitor.isNativePlatform();
  } catch (error) {
    return false;
  }
};

export default class Cookies {
  constructor(wholeDomain = false) {
    this.whole_domain = wholeDomain;
    this.isCapacitor = isCapacitorEnvironment();

    if (!this.isCapacitor) {
      this.cookies = new ReactCookies();
    }
  }

  async get(cookie) {
    if (this.isCapacitor) {
      try {
        const { value } = await Preferences.get({ key: cookie });
        // Preferences.get returns null if the key doesn't exist
        return value === null ? undefined : value;
      } catch (error) {
        console.warn("Failed to get cookie from Preferences:", error);
        return undefined;
      }
    } else {
      return this.cookies.get(cookie);
    }
  }

  async set(cookie, cookieExpiration) {
    if (this.isCapacitor) {
      try {
        if (typeof cookie === "object") {
          await Preferences.set({
            key: cookie.key,
            value: cookie.value,
          });
        } else {
          await Preferences.set({
            key: cookie,
            value: "true",
          });
        }
      } catch (error) {
        console.warn("Failed to set cookie with Preferences:", error);
      }
    } else {
      const expires = cookieExpiration || getExpirationDate();
      const optionPath = this.whole_domain ? { path: "/" } : {};

      if (typeof cookie === "object") {
        this.cookies.set(cookie.key, cookie.value, {
          expires: expires,
          ...optionPath,
        });
        return;
      }

      this.cookies.set(cookie, true, {
        expires: expires,
        ...optionPath,
      });
    }
  }

  async remove(cookie) {
    if (this.isCapacitor) {
      try {
        await Preferences.remove({ key: cookie });
      } catch (error) {
        console.warn("Failed to remove cookie with Preferences:", error);
      }
    } else {
      return this.cookies.remove(cookie);
    }
  }

  async clear() {
    if (this.isCapacitor) {
      try {
        await Preferences.clear();
      } catch (error) {
        console.warn("Failed to clear cookies with Preferences:", error);
      }
    } else {
      Object.keys(this.cookies.getAll()).forEach((cookie) => {
        this.cookies.remove(cookie);
      });
    }
  }

  // Helper method to get all cookies (for compatibility)
  async getAll() {
    if (this.isCapacitor) {
      try {
        const { keys } = await Preferences.keys();
        const cookies = {};
        for (const key of keys) {
          const { value } = await Preferences.get({ key });
          cookies[key] = value;
        }
        return cookies;
      } catch (error) {
        console.warn("Failed to get all cookies with Preferences:", error);
        return {};
      }
    } else {
      return this.cookies.getAll();
    }
  }

  // Authentication-specific methods
  async setAuthData(authData) {
    if (this.isCapacitor) {
      try {
        await Preferences.set({
          key: "auth_data",
          value: JSON.stringify(authData),
        });
      } catch (error) {
        console.warn("Failed to set auth data:", error);
      }
    } else {
      // For web, we can use localStorage as fallback
      try {
        localStorage.setItem("auth_data", JSON.stringify(authData));
      } catch (error) {
        console.warn("Failed to set auth data in localStorage:", error);
      }
    }
  }

  async getAuthData() {
    if (this.isCapacitor) {
      try {
        const { value } = await Preferences.get({ key: "auth_data" });
        return value ? JSON.parse(value) : null;
      } catch (error) {
        console.warn("Failed to get auth data:", error);
        return null;
      }
    } else {
      // For web, check localStorage as fallback
      try {
        const value = localStorage.getItem("auth_data");
        return value ? JSON.parse(value) : null;
      } catch (error) {
        console.warn("Failed to get auth data from localStorage:", error);
        return null;
      }
    }
  }

  async removeAuthData() {
    if (this.isCapacitor) {
      try {
        await Preferences.remove({ key: "auth_data" });
      } catch (error) {
        console.warn("Failed to remove auth data:", error);
      }
    } else {
      // For web, remove from localStorage as fallback
      try {
        localStorage.removeItem("auth_data");
      } catch (error) {
        console.warn("Failed to remove auth data from localStorage:", error);
      }
    }
  }
}
